import BaseAPIService from './baseAPI';
import { Post } from '../types/api';

// Scheduler Types
export interface SchedulePostRequest {
  scheduled_time: string; // ISO datetime format
}

export interface SchedulePostResponse {
  data: Post;
  status: string;
  message: string;
}

export interface UpcomingPostsResponse {
  data: Post[];
  total: number;
  page: number;
  page_size: number;
  status: string;
  message: string;
}

export interface BulkScheduleRequest {
  post_ids: string[];
  scheduled_times: string[];
}

export interface BulkScheduleResponse {
  data: {
    scheduled_count: number;
    failed_count: number;
    scheduled_posts: Post[];
    failed_posts: Array<{ post_id: string; error: string }>;
  };
  status: string;
  message: string;
}

export interface RecurringScheduleRequest {
  post_id: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  start_date: string;
  end_date?: string;
  days_of_week?: number[]; // 0-6, 0 = Sunday
  time_of_day: string; // HH:MM format
}

export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  post_id?: string;
  platform?: string;
  status: 'scheduled' | 'published' | 'failed';
}

class SchedulerAPIService extends BaseAPIService {
  constructor() {
    super();
  }

  // Basic Scheduling
  async schedulePost(postId: string, request: SchedulePostRequest): Promise<SchedulePostResponse> {
    return this.post<SchedulePostResponse>(`/scheduler/schedule/${postId}`, request);
  }

  async updateSchedule(postId: string, request: SchedulePostRequest): Promise<SchedulePostResponse> {
    return this.put<SchedulePostResponse>(`/scheduler/schedule/${postId}`, request);
  }

  async cancelSchedule(postId: string): Promise<{ status: string; message: string }> {
    return this.delete<{ status: string; message: string }>(`/scheduler/schedule/${postId}`);
  }

  // Upcoming Posts
  async getUpcomingPosts(page = 1, pageSize = 20): Promise<UpcomingPostsResponse> {
    return this.get<UpcomingPostsResponse>(`/scheduler/upcoming?page=${page}&page_size=${pageSize}`);
  }

  async getScheduledPosts(startDate?: string, endDate?: string): Promise<UpcomingPostsResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    return this.get<UpcomingPostsResponse>(`/scheduler/scheduled?${params.toString()}`);
  }

  // Bulk Operations
  async bulkSchedule(request: BulkScheduleRequest): Promise<BulkScheduleResponse> {
    return this.post<BulkScheduleResponse>('/scheduler/bulk-schedule', request);
  }

  async bulkCancel(postIds: string[]): Promise<{ data: { cancelled_count: number; failed_count: number } }> {
    return this.post<{ data: { cancelled_count: number; failed_count: number } }>('/scheduler/bulk-cancel', { post_ids: postIds });
  }

  // Recurring Schedules
  async createRecurringSchedule(request: RecurringScheduleRequest): Promise<{ data: { schedule_id: string; next_runs: string[] } }> {
    return this.post<{ data: { schedule_id: string; next_runs: string[] } }>('/scheduler/recurring', request);
  }

  async getRecurringSchedules(): Promise<{ data: Array<{ id: string; post_id: string; frequency: string; next_run: string }> }> {
    return this.get<{ data: Array<{ id: string; post_id: string; frequency: string; next_run: string }> }>('/scheduler/recurring');
  }

  async deleteRecurringSchedule(scheduleId: string): Promise<{ status: string; message: string }> {
    return this.delete<{ status: string; message: string }>(`/scheduler/recurring/${scheduleId}`);
  }

  // Calendar View
  async getCalendarEvents(month: string, year: string): Promise<{ data: CalendarEvent[] }> {
    return this.get<{ data: CalendarEvent[] }>(`/scheduler/calendar?month=${month}&year=${year}`);
  }

  async getCalendarEventsRange(startDate: string, endDate: string): Promise<{ data: CalendarEvent[] }> {
    return this.get<{ data: CalendarEvent[] }>(`/scheduler/calendar/range?start=${startDate}&end=${endDate}`);
  }

  // Schedule Optimization
  async getOptimalTimes(platform?: string): Promise<{ data: Array<{ time: string; score: number; day: string }> }> {
    const url = platform ? `/scheduler/optimal-times?platform=${platform}` : '/scheduler/optimal-times';
    return this.get<{ data: Array<{ time: string; score: number; day: string }> }>(url);
  }

  async suggestScheduleTimes(contentType: string, targetAudience?: string): Promise<{ data: Array<{ time: string; reason: string }> }> {
    return this.post<{ data: Array<{ time: string; reason: string }> }>('/scheduler/suggest-times', {
      content_type: contentType,
      target_audience: targetAudience
    });
  }

  // Schedule Analytics
  async getScheduleAnalytics(): Promise<{ data: { total_scheduled: number; success_rate: number; best_times: string[] } }> {
    return this.get<{ data: { total_scheduled: number; success_rate: number; best_times: string[] } }>('/scheduler/analytics');
  }

  // Queue Management
  async getPublishQueue(): Promise<{ data: Array<{ post_id: string; scheduled_time: string; platform: string; status: string }> }> {
    return this.get<{ data: Array<{ post_id: string; scheduled_time: string; platform: string; status: string }> }>('/scheduler/queue');
  }

  async pauseQueue(): Promise<{ status: string; message: string }> {
    return this.post<{ status: string; message: string }>('/scheduler/queue/pause');
  }

  async resumeQueue(): Promise<{ status: string; message: string }> {
    return this.post<{ status: string; message: string }>('/scheduler/queue/resume');
  }
}

export const schedulerAPI = new SchedulerAPIService();
export default schedulerAPI; 